// Payment System v3.0 PR 8 — Right-Size workflow wizard (design spec §8.4).
// Route: /portfolio-admin/loans/:loanId/right-size. Three steps:
//   1. Context — loan summary + four inputs (closing date, new buyer name,
//      sale price, principal shortfall).
//   2. Preview — server-computed YMP / fees / Total Borrower Cash + the
//      WF1 disbursement preview + the substituted-loan preview.
//   3. Confirm — final summary; Execute button POSTs and redirects to the
//      new substituted loan's detail page.
//
// Adapted from InvestorClosingWizard's stepper / form pattern.

import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  FormField,
  Input,
} from "@/design-system/components/ui";
import {
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { PreContributionScopeRedirect } from "@/components/portfolioAdmin/PreContributionScopeRedirect";
import { useLoan } from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import {
  useExecuteRightSize,
  usePreviewRightSize,
} from "@/hooks/portfolioAdmin/useRightSize";
import type { RightSizePreview } from "@/types/rightSize";
import { isoDateInTz, useUserTimeZone } from "@/utils/datetime";

type Step = 1 | 2 | 3;

function formatCurrency(n: number): string {
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });
}

function formatPct(n: number): string {
  return `${(n * 100).toFixed(2)}%`;
}

export function RightSizeWizard() {
  const { loanId } = useParams<{ loanId: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const loanQuery = useLoan(loanId);
  const preview = usePreviewRightSize(loanId ?? "");
  const execute = useExecuteRightSize(loanId ?? "");
  const tz = useUserTimeZone();

  const [step, setStep] = useState<Step>(1);
  const [closingDate, setClosingDate] = useState(() => isoDateInTz(new Date(), tz));
  const [newBuyerName, setNewBuyerName] = useState("");
  const [salePrice, setSalePrice] = useState("");
  const [principalShortfall, setPrincipalShortfall] = useState("");
  const [previewData, setPreviewData] = useState<RightSizePreview | null>(null);

  const loan = loanQuery.data;

  const inputsValid =
    !!closingDate &&
    newBuyerName.trim().length > 0 &&
    Number(salePrice) > 0 &&
    Number(principalShortfall) >= 0;

  async function handlePreview() {
    if (!loanId || !inputsValid) return;
    try {
      const data = await preview.mutateAsync({
        closingDate,
        newBuyerEntityName: newBuyerName.trim(),
        salePrice: Number(salePrice),
        principalShortfall: Number(principalShortfall),
      });
      setPreviewData(data);
      setStep(2);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  async function handleExecute() {
    if (!loanId || !previewData) return;
    try {
      const result = await execute.mutateAsync({
        closingDate,
        newBuyerEntityName: newBuyerName.trim(),
        salePrice: Number(salePrice),
        principalShortfall: Number(principalShortfall),
        expectedVersionId: previewData.versionId,
      });
      toast.success(
        `Right-Size committed — original loan retired, substituted loan created (${formatCurrency(result.salePrice)} principal).`,
      );
      navigate(`/portfolio-admin/loans/${result.newLoanId}`);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <Button
            variant="text"
            size="sm"
            onClick={() => navigate(`/portfolio-admin/loans/${loanId}`)}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to loan
          </Button>
          <h1 className="mt-2 text-2xl font-semibold">Right-Size Closing</h1>
          <p className="text-sm text-muted-foreground">
            Retire the original loan and create a substituted Buyer Note at the
            Sale Price.
          </p>
        </div>
        <StepIndicator step={step} />
      </div>

      {step === 1 && (
        <Card className="space-y-4 p-6">
          <h2 className="text-lg font-semibold">1. Context &amp; inputs</h2>
          {loan ? (
            <div className="grid grid-cols-2 gap-3 rounded-md border bg-muted/30 p-4 text-sm">
              <div>
                <div className="text-muted-foreground">Loan</div>
                <div className="font-medium">{loan.loanNumber}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Current balance</div>
                <div className="font-medium">
                  {formatCurrency(loan.currentBalance)}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Account state</div>
                <div className="font-medium">
                  {loan.accountState ?? "—"}
                </div>
              </div>
            </div>
          ) : null}

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Closing date">
              <Input
                type="date"
                value={closingDate}
                onChange={(e) => setClosingDate(e.target.value)}
              />
            </FormField>
            <FormField label="New buyer entity name">
              <Input
                value={newBuyerName}
                onChange={(e) => setNewBuyerName(e.target.value)}
                placeholder="e.g. NewCo Buyer LLC"
              />
            </FormField>
            <FormField label="Sale price (becomes new loan principal)">
              <Input
                type="number"
                value={salePrice}
                onChange={(e) => setSalePrice(e.target.value)}
                placeholder="700000"
              />
            </FormField>
            <FormField label="Principal shortfall (original borrower cash)">
              <Input
                type="number"
                value={principalShortfall}
                onChange={(e) => setPrincipalShortfall(e.target.value)}
                placeholder="50000"
              />
            </FormField>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handlePreview}
              disabled={!inputsValid || preview.isPending}
            >
              {preview.isPending ? "Computing..." : "Compute preview →"}
            </Button>
          </div>
        </Card>
      )}

      {step === 2 && previewData && (
        <Card className="space-y-4 p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">2. Preview</h2>
            <Badge status="info">{previewData.loanState}</Badge>
          </div>

          <div className="grid grid-cols-2 gap-3 rounded-md border p-4 text-sm">
            <Figure
              label="Yield Maintenance Premium (through balloon)"
              value={previewData.yieldMaintenancePremium}
            />
            <Figure
              label="Accrued Interest"
              value={previewData.accruedInterest}
            />
            <Figure
              label="Prepayment Admin Fee (3%)"
              value={previewData.prepaymentAdminFee}
            />
            <Figure
              label="Coordination Fee (3%)"
              value={previewData.coordinationFee}
            />
            <Figure
              label="Principal Shortfall"
              value={previewData.principalShortfall}
            />
            <Figure
              label="Total Borrower Cash (excludes Sale Price)"
              value={previewData.totalBorrowerCashPayment}
              emphasis
            />
          </div>

          <div className="rounded-md border bg-blue-50 p-3 text-xs text-blue-900">
            <strong>Sale Price ({formatCurrency(previewData.salePrice)})</strong>{" "}
            is the new substituted loan's principal — an asset created at
            closing, NOT cash flowing through the waterfall.
          </div>

          {previewData.wf1Disbursements.length > 0 && (
            <div>
              <h3 className="mb-2 text-sm font-semibold">
                WF1 disbursements (retained-source)
              </h3>
              <Table>
                <THead>
                  <TR>
                    <TH>#</TH>
                    <TH>Obligation</TH>
                    <TH>Recipient</TH>
                    <TH className="text-right">Amount</TH>
                  </TR>
                </THead>
                <TBody>
                  {previewData.wf1Disbursements.map((d, i) => (
                    <TR key={`wf1-${i}`}>
                      <TD>{d.priorityPosition}</TD>
                      <TD>{d.obligationTypeCode}</TD>
                      <TD>
                        {d.recipientDisplayName ?? d.recipientResolution}
                      </TD>
                      <TD className="text-right">{formatCurrency(d.amount)}</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </div>
          )}

          {previewData.wf2Disbursements.length > 0 && (
            <div>
              <h3 className="mb-2 text-sm font-semibold">
                WF2 disbursements (contributed-source)
              </h3>
              <Table>
                <THead>
                  <TR>
                    <TH>#</TH>
                    <TH>Obligation</TH>
                    <TH>Recipient</TH>
                    <TH className="text-right">Amount</TH>
                  </TR>
                </THead>
                <TBody>
                  {previewData.wf2Disbursements.map((d, i) => (
                    <TR key={`wf2-${i}`}>
                      <TD>{d.priorityPosition}</TD>
                      <TD>{d.obligationTypeCode}</TD>
                      <TD>
                        {d.recipientDisplayName ?? d.recipientResolution}
                      </TD>
                      <TD className="text-right">{formatCurrency(d.amount)}</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </div>
          )}

          <div className="space-y-2 rounded-md border bg-muted/30 p-4 text-sm">
            <h3 className="font-semibold">Substituted loan preview</h3>
            <div className="grid grid-cols-2 gap-2">
              <Figure
                label="Loan number"
                stringValue={previewData.substitutedLoanPreview.loanNumber}
              />
              <Figure
                label="Original principal"
                value={previewData.substitutedLoanPreview.originalPrincipal}
              />
              <Figure
                label="Segment principal share (10 segments)"
                value={previewData.substitutedLoanPreview.segmentPrincipalShare}
              />
              <Figure
                label="Contract interest rate"
                stringValue={formatPct(
                  previewData.substitutedLoanPreview.contractInterestRate,
                )}
              />
              <Figure
                label="Amortization months"
                stringValue={String(
                  previewData.substitutedLoanPreview.amortizationMonths,
                )}
              />
              <Figure
                label="Balloon month"
                stringValue={String(
                  previewData.substitutedLoanPreview.balloonMonth,
                )}
              />
              <Figure
                label="k contributed (fresh)"
                stringValue={String(
                  previewData.substitutedLoanPreview.kContributedSegments,
                )}
              />
              <Figure
                label="State"
                stringValue={previewData.substitutedLoanPreview.loanState}
              />
            </div>
          </div>

          <div className="flex justify-between">
            <Button variant="secondary" onClick={() => setStep(1)}>
              ← Back
            </Button>
            <Button onClick={() => setStep(3)}>Continue to confirm →</Button>
          </div>
        </Card>
      )}

      {step === 3 && previewData && (
        <Card className="space-y-4 p-6">
          <h2 className="text-lg font-semibold">3. Confirm Right-Size</h2>
          <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
            This is an atomic, irreversible operation. The original loan
            <span className="font-semibold">
              {" "}
              ({previewData.loanNumber}){" "}
            </span>
            will be marked
            <span className="font-mono"> retired_right_size </span>
            with a zero balance, and a new substituted loan
            <span className="font-semibold">
              {" "}
              ({previewData.substitutedLoanPreview.loanNumber}){" "}
            </span>
            will be created with{" "}
            {formatCurrency(previewData.substitutedLoanPreview.originalPrincipal)}{" "}
            principal in a single transaction.
          </div>

          <div className="space-y-2 text-sm">
            <div>
              Borrower cash through waterfall:{" "}
              <strong>
                {formatCurrency(previewData.totalBorrowerCashPayment)}
              </strong>
            </div>
            <div>
              New loan principal:{" "}
              <strong>
                {formatCurrency(previewData.substitutedLoanPreview.originalPrincipal)}
              </strong>
            </div>
          </div>

          <div className="flex justify-between">
            <Button variant="secondary" onClick={() => setStep(2)}>
              ← Back to preview
            </Button>
            <Button onClick={handleExecute} disabled={execute.isPending}>
              {execute.isPending ? (
                "Executing..."
              ) : (
                <>
                  <Check className="mr-2 h-4 w-4" /> Execute Right-Size
                </>
              )}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

function StepIndicator({ step }: { step: Step }) {
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      {[1, 2, 3].map((n) => (
        <div
          key={n}
          className={
            n === step
              ? "rounded-full bg-primary px-3 py-1 font-semibold text-primary-foreground"
              : "rounded-full bg-muted px-3 py-1"
          }
        >
          Step {n}
        </div>
      ))}
    </div>
  );
}

function Figure({
  label,
  value,
  stringValue,
  emphasis,
}: {
  label: string;
  value?: number;
  stringValue?: string;
  emphasis?: boolean;
}) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div
        className={emphasis ? "text-base font-semibold" : "text-sm font-medium"}
      >
        {stringValue ?? (value !== undefined ? formatCurrency(value) : "—")}
      </div>
    </div>
  );
}

// Route entry — pre-contribution scope redirects deep links to the loan
// detail page while the stage-2 wizards are hidden.
export default function RightSizeWizardRoute() {
  return (
    <PreContributionScopeRedirect>
      <RightSizeWizard />
    </PreContributionScopeRedirect>
  );
}
